import React, { useState } from 'react';
import { useAppConstantsContext } from '@/context/AppConstants';
import {
  DataCard,
  ReportSection,
  propertiesTypes,
  openReportInExistingTab,
  openReportInNewTab,
} from '@/reports/index';
import { SearchType } from '@/reports/openReport/openReport';
import { Button, Text, Link, styled, Box } from '@/theme/index';
import { scrollToElement } from '@/utils/scrollToElement';

export interface RelatedPropertiesSectionProps {
  properties: propertiesTypes.PropertyCores;
  propertyAddress: string;
}

const CardContainer = styled('div')(({ theme }) => ({
  padding: theme.spacing(1.56, 2),
  '&:nth-of-type(odd)': {
    marginTop: theme.spacing(0),
    backgroundColor:
      theme.palette.relatedProperties?.main ?? theme.palette.primary.main,
  },
  '&:nth-of-type(even)': {
    marginTop: theme.spacing(0),
    backgroundColor:
      theme.palette.relatedProperties?.light ?? theme.palette.primary.light,
  },
}));

const AddressLink = styled(Link)(({ theme }) => ({
  color: theme.palette.relatedProperties?.dark ?? theme.palette.primary.main,
  textDecorationColor:
    theme.palette.relatedProperties?.dark ?? theme.palette.primary.main,
  fontWeight: '700',
  fontSize: '16px',
  cursor: 'pointer',
}));

export function RelatedPropertiesSection({
  properties,
  propertyAddress,
}: RelatedPropertiesSectionProps) {
  const relatedPropertiesToShow = 5;
  const [displayCount, setDisplayCount] = useState(5);
  const handleShowMore = () => {
    setDisplayCount((prevCount) => prevCount + 5);
  };

  const handleShowLess = () => {
    setDisplayCount(relatedPropertiesToShow);
    scrollToElement('related-properties-section');
  };

  const {
    links: { baseUrl },
    featureFlags: { shouldOpenReportInCurrentTab },
  } = useAppConstantsContext();

  return (
    <ReportSection
      title="Related Properties"
      sectionId="related-properties-section"
      sectionDescription="Some properties related to this property."
      hasContent={properties.length > 0}
      propertyAddress={propertyAddress}
      relatedProperties
    >
      <DataCard>
        {properties.slice(0, displayCount).map((property) => {
          const address = property?.parcel_address;
          const fullAddress = address?.full;
          const parsedAddress = address?.parsed;
          const addressString = `${
            parsedAddress?.primary_address_number ?? ''
          } ${parsedAddress?.pre_direction ?? ''} ${
            parsedAddress?.street_name ?? ''
          } ${parsedAddress?.street_type ?? ''} ${
            parsedAddress?.unit_designator ?? ''
          } ${parsedAddress?.unit_number ?? ''} ${parsedAddress?.city ?? ''}, ${
            parsedAddress?.state ?? ''
          } ${parsedAddress?.zip5 ?? ''}`;

          const searchParams = {
            address: parsedAddress || '',
            city: parsedAddress?.city || '',
            state: parsedAddress?.state || '',
            zipcode: parsedAddress?.zip5 || '',
            property_id: property.id || '',
          };

          const clickHandler = async () => {
            const tabData = {
              ...searchParams,
              searchType: 'property' as SearchType,
              address: addressString,
            };

            if (shouldOpenReportInCurrentTab) {
              openReportInExistingTab(tabData, baseUrl);
              return;
            }

            openReportInNewTab(tabData, baseUrl);
          };
          return (
            <CardContainer
              key={property.id}
              className="content"
              id="related-properties-section"
            >
              <AddressLink onClick={clickHandler}>
                {parsedAddress?.street_name ? (
                  <Text>{addressString.trim()}</Text>
                ) : (
                  <Text>{fullAddress}</Text>
                )}
              </AddressLink>
            </CardContainer>
          );
        })}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          {displayCount < properties.length && (
            <Button
              variant="contained"
              color="success"
              onClick={handleShowMore}
              sx={{ width: '130px' }}
            >
              Show More
            </Button>
          )}

          {properties.length <= displayCount && displayCount > 5 && (
            <Button
              variant="contained"
              onClick={handleShowLess}
              sx={{ width: '130px' }}
              color="success"
            >
              Show Less
            </Button>
          )}
        </Box>
      </DataCard>
    </ReportSection>
  );
}
